import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Headphones, Pause, Play } from "lucide-react";
import { api, fmtNum } from "../lib/api";
import { usePlayer } from "../context/PlayerContext";
import { useAuth } from "../context/AuthContext";
import { Cover, MixActions } from "../components/MixCard";
import { useT } from "../lib/i18n";

const fmtDate = (iso, lang) => {
  if (!iso) return "—";
  const d = new Date(`${iso.slice(0, 10)}T12:00:00`);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString(lang === "en" ? "en-US" : "es-ES", { day: "numeric", month: "short", year: "numeric" });
};

const MixInfo = ({ m }) => {
  const { t, lang } = useT();
  const rows = [
    ["bpm", t("info.bpm"), m.bpm || "—"],
    ["key", t("info.key"), m.key || "—"],
    ["genre", t("info.genre"), m.genre ? <Link to={`/generos/${encodeURIComponent(m.genre)}`} className="text-[#1FD1FF] hover:underline">{m.genre}</Link> : "—"],
    ["release", t("info.release"), fmtDate(m.release_date || m.created_at, lang)],
    ["duration", t("info.duration"), m.duration || "—"],
  ];
  return (
    <dl data-testid="mix-info" className="mt-8 grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-5 border-t border-white/10 pt-6 max-w-xl">
      {rows.map(([k, label, v]) => (
        <div key={k}>
          <dt className="font-semibold text-white text-base">{label}</dt>
          <dd data-testid={`mix-info-${k}`} className="text-[#A8B3C4] text-lg mt-0.5">{v}</dd>
        </div>
      ))}
    </dl>
  );
};

export default function MixPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const { t } = useT();
  const { playMix, mix: current, playing, withOverrides } = usePlayer();
  const [mix, setMix] = useState(null);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    api.get(`/mixes/${id}`).then((r) => setMix(r.data)).catch(() => setMissing(true));
  }, [id, user]);

  if (missing) return <p data-testid="mix-not-found" className="pt-40 text-center font-mono text-[#A8B3C4]">{t("mix.gone")} <Link to="/" className="text-[#1FD1FF]">{t("mix.home")}</Link></p>;
  if (!mix) return <div className="pt-40" />;
  const m = withOverrides(mix);
  const active = current?.id === m.id && playing;

  return (
    <main data-testid="mix-page" className="max-w-[1500px] mx-auto px-4 lg:px-10 pt-28 pb-20">
      <Link to="/" data-testid="mix-back-link" className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-[#A8B3C4] hover:text-[#1FD1FF] mb-10"><ArrowLeft className="w-4 h-4" />{t("mix.back")}</Link>
      <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-end">
        <motion.div initial={{ clipPath: "inset(0 100% 0 0)" }} animate={{ clipPath: "inset(0 0% 0 0)" }} transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
          className="lg:col-span-6 aspect-square overflow-hidden [clip-path:polygon(0_0,100%_0,100%_90%,90%_100%,0_100%)]">
          <Cover mix={m} />
        </motion.div>
        <div className="lg:col-span-6">
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#1FD1FF]">{m.genre} · {m.bpm || "—"} BPM{m.key ? ` · ${m.key}` : ""}</p>
          <h1 className="font-display text-6xl sm:text-7xl lg:text-8xl leading-[0.85] mt-4">{m.title}</h1>
          {m.description && <p className="mt-6 text-[#A8B3C4] max-w-lg">{m.description}</p>}
          <p className="mt-4 font-mono text-xs text-[#A8B3C4]">{(m.tags || []).map((t) => `#${t}`).join("  ")}</p>
          <MixInfo m={m} />
          <div className="mt-10 flex flex-wrap items-center gap-6">
            <button data-testid="mix-page-play-button" onClick={() => playMix(m, [m])} className="flex items-center gap-3 pl-2 pr-6 py-2 bg-[#1FD1FF] text-black font-mono text-sm uppercase tracking-widest hover:bg-white transition-colors">
              <span className="w-10 h-10 bg-black text-[#1FD1FF] grid place-items-center">{active ? <Pause className="w-4 h-4 fill-current" /> : <Play className="w-4 h-4 fill-current" />}</span>
              {t(active ? "mix.pause" : "mix.play")}
            </button>
            <span className="font-mono text-sm flex items-center gap-2 text-[#A8B3C4]"><Headphones className="w-4 h-4" />{fmtNum(m.plays)} {t("mix.plays")}</span>
          </div>
          <div className="mt-6 text-[#A8B3C4]"><MixActions mix={m} idx="page" /></div>
        </div>
      </div>
    </main>
  );
}
